import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Store, CalendarDays, Hand, Truck, Wallet, ReceiptText, Info, PackageSearch } from "lucide-react";
import { PublicLayout } from "../components/PublicLayout";
import { ProductGrid } from "../components/shop/ProductCard";
import { OrganizerContactBlock } from "../components/OrganizerContactBlock";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Skeleton } from "../components/ui/skeleton";
import { api } from "../lib/api";
import { nok } from "../lib/shop";
import { useLang } from "../context/LanguageContext";

// Butikksida til éin arrangør: /butikk/:organizerId
export default function ShopOrganizerPage() {
  const { organizerId } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    api
      .get(`/shop/organizers/${organizerId}`)
      .then((res) => setData(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [organizerId]);

  if (loading) {
    return (
      <PublicLayout>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6" data-testid="organizer-shop-loading">
          <Skeleton className="h-28 rounded-2xl" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 lg:gap-6">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-72 rounded-xl" />)}
          </div>
        </div>
      </PublicLayout>
    );
  }

  if (notFound || !data) {
    return (
      <PublicLayout>
        <div className="max-w-2xl mx-auto px-4 py-24 text-center space-y-4" data-testid="organizer-shop-not-found">
          <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-secondary text-muted-foreground"><Store className="w-7 h-7" /></span>
          <h1 className="text-2xl font-semibold">{t("Fant ikke butikken")}</h1>
          <p className="text-sm text-muted-foreground">{t("Denne arrangøren har ikke åpnet butikken ennå.")}</p>
          <div className="flex flex-wrap justify-center gap-2 pt-2">
            <Button asChild data-testid="organizer-shop-all-link"><Link to="/butikk"><ArrowLeft className="w-4 h-4 mr-1.5" />{t("Til alle butikker")}</Link></Button>
            <Button asChild variant="outline" data-testid="organizer-shop-events-link"><Link to={`/arrangor/${organizerId}`}>{t("Se arrangementer")}</Link></Button>
          </div>
        </div>
      </PublicLayout>
    );
  }

  const { organizer, intro, products } = data;
  const initials = (organizer.org_name || "A").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

  return (
    <PublicLayout>
      <section className="hero-decor border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14 space-y-6">
          <Link to="/butikk" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors duration-150" data-testid="organizer-shop-back-link">
            <ArrowLeft className="w-4 h-4" />
            {t("Til alle butikker")}
          </Link>
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex items-center gap-5 min-w-0">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-ink text-ink-foreground flex items-center justify-center font-heading font-semibold text-2xl shrink-0">{initials}</div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" data-testid="organizer-shop-name">{organizer.org_name}</h1>
                  <Badge variant="secondary" className="gap-1"><Store className="w-3 h-3" /> {t("Butikk")}</Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-1" data-testid="organizer-shop-meta">
                  {organizer.org_number && <span>{t("Org.nr {n}", { n: organizer.org_number })} · </span>}
                  {products.length === 1 ? t("{n} vare", { n: 1 }) : t("{n} varer", { n: products.length })}
                </p>
              </div>
            </div>
            <Button asChild variant="outline" className="bg-background/70 self-start" data-testid="organizer-shop-events-button">
              <Link to={`/arrangor/${organizer.id}`}><CalendarDays className="w-4 h-4 mr-1.5" /> {t("Se arrangementer")}</Link>
            </Button>
          </motion.div>
          <div className="flex flex-wrap gap-2" data-testid="organizer-shop-capabilities">
            {data.pickup_enabled && <Badge variant="outline" className="gap-1.5 bg-background/70 font-normal"><Hand className="w-3.5 h-3.5 text-brand" /> {data.pickup_place ? t("Henting: {place}", { place: data.pickup_place }) : t("Henting hos arrangøren")}</Badge>}
            {data.shipping_enabled && <Badge variant="outline" className="gap-1.5 bg-background/70 font-normal"><Truck className="w-3.5 h-3.5 text-brand" /> {Number(data.shipping_price || 0) === 0 ? t("Gratis frakt") : t("Sending: {price}", { price: nok(data.shipping_price) })}</Badge>}
            {data.vipps_available && <Badge variant="outline" className="gap-1.5 bg-background/70 font-normal"><Wallet className="w-3.5 h-3.5 text-brand" /> {t("Vipps")}</Badge>}
            {data.invoice_available && <Badge variant="outline" className="gap-1.5 bg-background/70 font-normal"><ReceiptText className="w-3.5 h-3.5 text-brand" /> {t("Faktura")}</Badge>}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 space-y-4">
        {!data.can_sell && (
          <div className="rounded-xl border border-[hsl(var(--info))]/40 bg-[hsl(var(--info))]/10 p-4 flex gap-3 text-sm" data-testid="organizer-shop-closed-note">
            <Info className="w-5 h-5 text-[hsl(var(--info))] shrink-0" />
            <p>{t("Butikken tar ikke imot bestillinger akkurat nå.")}</p>
          </div>
        )}
        {intro && (
          <div className="rounded-xl border border-border bg-card p-5 sm:p-6 max-w-3xl" data-testid="organizer-shop-intro">
            <p className="text-sm sm:text-[15px] leading-relaxed whitespace-pre-line">{intro}</p>
          </div>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        {products.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border bg-card p-12 text-center space-y-2" data-testid="organizer-shop-empty">
            <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[hsl(var(--info))]/10 text-[hsl(var(--info))]"><PackageSearch className="w-7 h-7" /></span>
            <h2 className="font-heading font-semibold text-lg">{t("Ingen varer ennå")}</h2>
            <p className="text-sm text-muted-foreground">{t("Kom tilbake senere!")}</p>
          </div>
        ) : (
          <ProductGrid products={products} showOrganizer={false} testId="organizer-shop-grid" />
        )}
        <OrganizerContactBlock name={organizer.org_name} orgNumber={organizer.org_number} contact={organizer.contact} className="max-w-xl" />
      </section>
    </PublicLayout>
  );
}
